import { useCallback, useEffect, useRef, useState } from 'react';
import {
  downloadLoanInstallmentTemplate,
  previewLoanInstallments,
} from '../services/GuaranteesService';
import { isSuccessfulResponse } from '../utils/Utils';
import {
  mapSyndicatedPreviewInstallments,
  type SyndicatedPreviewInstallmentRow,
} from '../utils/syndicatedLines';
import { useToast } from '../components/toast/toast';

export interface LoanInstallmentTemplateParams {
  guaranteeNumber?: string;
  companyName?: string;
  cuit?: string;
  operationType?: string;
  monetizationAmount?: number | null;
  currency?: string;
}

const extractErrorMessages = (response: unknown): string[] => {
  const raw = response as Record<string, unknown> | null | undefined;
  if (Array.isArray(raw?.errors) && raw.errors.length > 0) {
    return raw.errors.map((entry) => (typeof entry === 'string' ? entry : JSON.stringify(entry)));
  }
  if (typeof raw?.message === 'string' && raw.message) {
    return [raw.message];
  }
  const nested = raw?.data as Record<string, unknown> | undefined;
  if (Array.isArray(nested?.errors) && nested.errors.length > 0) {
    return nested.errors.map((entry) => (typeof entry === 'string' ? entry : JSON.stringify(entry)));
  }
  if (typeof nested?.message === 'string' && nested.message) {
    return [nested.message];
  }
  return [];
};

export interface UseManualInstallmentPlanOptions {
  enabled: boolean;
  monetizationAmount: number | null;
  issueDate?: string;
  assetId?: number | null;
  assetQuantity?: number | null;
  currencyId?: number | null;
  templateParams: LoanInstallmentTemplateParams;
  toastId?: string;
}

export function useManualInstallmentPlan({
  enabled,
  monetizationAmount,
  issueDate,
  assetId,
  assetQuantity,
  currencyId,
  templateParams,
  toastId = 'manual-installment-plan-toast',
}: UseManualInstallmentPlanOptions) {
  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<SyndicatedPreviewInstallmentRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const prevEnabledRef = useRef(enabled);
  const comparisonKey = [
    enabled,
    monetizationAmount,
    issueDate ?? '',
    assetId ?? '',
    assetQuantity ?? '',
    currencyId ?? '',
  ].join('|');
  const prevComparisonKeyRef = useRef(comparisonKey);

  const resetPlan = useCallback(() => {
    setFile(null);
    setPreviewRows([]);
    setErrors([]);
    setWarnings([]);
    setSummary(null);
  }, []);

  const discardWithToast = useCallback(
    (description: string) => {
      if (file || previewRows.length > 0 || summary) {
        resetPlan();
        useToast.warning({
          id: toastId,
          title: 'Plan de cuotas descartado',
          description,
          autoClose: 5000,
        });
      } else {
        resetPlan();
      }
    },
    [file, previewRows.length, resetPlan, summary, toastId]
  );

  useEffect(() => {
    if (prevEnabledRef.current && !enabled) {
      discardWithToast('Al salir de OTRO se descartó el archivo cargado.');
    }
    prevEnabledRef.current = enabled;
  }, [discardWithToast, enabled]);

  const validateFile = useCallback(
    async (selected: File, keepFileOnError: boolean) => {
      if (monetizationAmount == null) return;
      setLoading(true);
      setErrors([]);
      setWarnings([]);
      try {
        const response: any = await previewLoanInstallments(
          selected,
          monetizationAmount,
          issueDate || undefined,
          assetId ?? undefined,
          assetQuantity ?? undefined,
          currencyId ?? undefined
        );
        if (!isSuccessfulResponse(response)) {
          const apiErrors = extractErrorMessages(response);
          setFile(keepFileOnError ? selected : null);
          setPreviewRows([]);
          setSummary(null);
          setErrors(
            apiErrors.length > 0
              ? apiErrors
              : ['No se pudo validar el archivo. Verificá el formato.']
          );
          return;
        }
        const data = response.data ?? response;
        const comparisonTerm =
          data.comparison_term ?? data.comparisonTerm ?? data.monetization_amount ?? monetizationAmount;
        const rows = mapSyndicatedPreviewInstallments(data.rows ?? [], comparisonTerm);
        setFile(selected);
        setWarnings(data.warnings ?? []);
        setPreviewRows(rows);
        setSummary(
          `${data.new_installments ?? data.newInstallments ?? rows.length} cuotas · diferencia ${data.difference ?? 0}`
        );
      } catch (err) {
        console.error('Error previewing loan installments:', err);
        setFile(keepFileOnError ? selected : null);
        setPreviewRows([]);
        setSummary(null);
        setErrors(['Error inesperado al validar el archivo.']);
      } finally {
        setLoading(false);
      }
    },
    [assetId, assetQuantity, currencyId, issueDate, monetizationAmount]
  );

  useEffect(() => {
    if (!enabled || !file) {
      prevComparisonKeyRef.current = comparisonKey;
      return;
    }
    if (prevComparisonKeyRef.current === comparisonKey) {
      return;
    }
    prevComparisonKeyRef.current = comparisonKey;
    void validateFile(file, true);
  }, [comparisonKey, enabled, file, validateFile]);

  const canUpload = enabled && monetizationAmount != null && monetizationAmount > 0;
  const isValid = enabled && !!file && previewRows.length > 0 && errors.length === 0;

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    setErrors([]);
    try {
      const response: any = await downloadLoanInstallmentTemplate({
        guaranteeNumber: templateParams.guaranteeNumber,
        companyName: templateParams.companyName,
        cuit: templateParams.cuit,
        operationType: templateParams.operationType,
        monetizationAmount: templateParams.monetizationAmount ?? undefined,
        currency: templateParams.currency,
      });
      if (!response?.success || !response.data) {
        useToast.error({
          id: toastId,
          title: 'No se pudo descargar la plantilla',
          description: response?.message ?? 'Intentá nuevamente.',
        });
        return;
      }
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = templateParams.guaranteeNumber
        ? `plantilla-cuotas-aval-${templateParams.guaranteeNumber}.xlsx`
        : 'plantilla-cuotas.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading loan installment template:', err);
      useToast.error({
        id: toastId,
        title: 'Error inesperado',
        description: 'No se pudo descargar la plantilla.',
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = async (selected: File) => {
    if (!canUpload || monetizationAmount == null) return;
    await validateFile(selected, true);
  };

  return {
    file,
    previewRows,
    errors,
    warnings,
    summary,
    loading,
    downloading,
    canUpload,
    isValid,
    resetPlan,
    handleDownloadTemplate,
    handleFileSelect,
  };
}
